import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { listMicrophones } from "@/lib/capture.ts";
import type { Prefs } from "@/lib/prefs.ts";
import { ReadAloud } from "@/lib/speech.ts";

type Props = {
  open: boolean;
  prefs: Prefs;
  busy: boolean;
  /** Real-time engine (Soniox): listeners hear speech live, so there is no delay to set. */
  realtime?: boolean;
  onChange: (patch: Partial<Prefs>) => void;
  onClose: () => void;
};

function Toggle({ label, hint, checked, disabled, onChange }: { label: string; hint?: string; checked: boolean; disabled?: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex min-h-11 items-center justify-between gap-4 text-sm">
      <span>
        {label}
        {hint ? <span className="block text-xs text-muted">{hint}</span> : null}
      </span>
      <input type="checkbox" className="h-5 w-5 accent-[var(--accent)]" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
    </label>
  );
}

export function SettingsDialog({ open, prefs, busy, realtime = false, onChange, onClose }: Props) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [mics, setMics] = useState<MediaDeviceInfo[]>([]);

  useEffect(() => {
    const el = dialog.current;
    if (!el) return;
    if (open && !el.open) {
      el.showModal();
      void listMicrophones().then(setMics).catch(() => setMics([]));
    } else if (!open && el.open) {
      el.close();
    }
  }, [open]);

  return (
    <dialog
      ref={dialog}
      onClose={onClose}
      onClick={(e) => {
        if (e.target === dialog.current) onClose();
      }}
      className="m-auto w-[min(32rem,calc(100vw-2rem))] rounded-2xl border border-line bg-surface p-0 text-fg backdrop:bg-black/50"
      aria-labelledby="settings-title"
    >
      <div className="flex items-center justify-between border-b border-line px-5 py-3">
        <h2 id="settings-title" className="font-display text-xl italic">
          Settings
        </h2>
        <button type="button" className="btn h-10 w-10 border-0 px-0" onClick={onClose} aria-label="Close settings">
          <X size={18} />
        </button>
      </div>
      <div className="flex max-h-[70dvh] flex-col gap-5 overflow-y-auto px-5 py-4">
        <fieldset className="flex flex-col gap-1" disabled={busy}>
          <legend className="mb-2 text-xs tracking-wider text-muted uppercase">Microphone</legend>
          <select className="field text-sm" value={prefs.deviceId} onChange={(e) => onChange({ deviceId: e.target.value })}>
            <option value="">System default</option>
            {mics.map((m, i) => (
              <option key={m.deviceId || i} value={m.deviceId}>
                {m.label || `Microphone ${i + 1}`}
              </option>
            ))}
          </select>
          <Toggle label="Noise suppression" hint="Helps speech detection in noisy rooms" checked={prefs.noiseSuppression} onChange={(v) => onChange({ noiseSuppression: v })} />
          <Toggle label="Echo cancellation" hint="Turn off when capturing a loudspeaker on purpose" checked={prefs.echoCancellation} onChange={(v) => onChange({ echoCancellation: v })} />
          <Toggle label="Automatic gain" checked={prefs.autoGainControl} onChange={(v) => onChange({ autoGainControl: v })} />
          {busy ? <p className="text-xs text-muted">Stop the session to change the microphone.</p> : null}
        </fieldset>
        <fieldset className="flex flex-col gap-1">
          <legend className="mb-2 text-xs tracking-wider text-muted uppercase">Display</legend>
          <label className="flex min-h-11 items-center justify-between gap-4 text-sm">
            Layout
            <select className="field min-h-9 text-sm" value={prefs.layout} onChange={(e) => onChange({ layout: e.target.value as Prefs["layout"] })}>
              <option value="interleaved">Interleaved</option>
              <option value="columns">Side by side</option>
            </select>
          </label>
          <Toggle label="Show original speech" checked={prefs.showSource} onChange={(v) => onChange({ showSource: v })} />
          <label className="flex min-h-11 items-center justify-between gap-4 text-sm">
            Text size
            <input
              type="range"
              min={0.85}
              max={1.6}
              step={0.05}
              value={prefs.textScale}
              onChange={(e) => onChange({ textScale: Number(e.target.value) })}
              className="w-40 accent-[var(--accent)]"
              aria-valuetext={`${Math.round(prefs.textScale * 100)}%`}
            />
          </label>
        </fieldset>
        <fieldset className="flex flex-col gap-1" disabled={realtime}>
          <legend className="mb-2 text-xs tracking-wider text-muted uppercase">Attendees</legend>
          <label className="flex min-h-11 items-center justify-between gap-4 text-sm">
            <span>
              Listener audio delay
              <span className="block text-xs text-muted">
                {realtime
                  ? "Not used with Soniox: attendees hear the translation live, as the speaker talks."
                  : "Phones hold the translated speech this long before playing it, so it flows without silent gaps. Applies at once, even during a session."}
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-2">
              <input
                type="range"
                min={0}
                max={10}
                step={0.5}
                value={prefs.listenerDelayS}
                onChange={(e) => onChange({ listenerDelayS: Number(e.target.value) })}
                className="w-32 accent-[var(--accent)]"
                aria-label="Listener audio delay in seconds"
                aria-valuetext={`${prefs.listenerDelayS} seconds`}
              />
              <span className="w-12 text-right tabular-nums">{realtime ? "Live" : `${prefs.listenerDelayS.toFixed(1)} s`}</span>
            </span>
          </label>
        </fieldset>
        <fieldset className="flex flex-col gap-1">
          <legend className="mb-2 text-xs tracking-wider text-muted uppercase">Audio out</legend>
          <Toggle
            label="Read translations aloud"
            hint={ReadAloud.supported ? "Uses this device's voices; best with headphones" : "Not supported in this browser"}
            checked={prefs.readAloud}
            disabled={!ReadAloud.supported}
            onChange={(v) => onChange({ readAloud: v })}
          />
        </fieldset>
      </div>
    </dialog>
  );
}
